import { HttpMethod, HttpRequestUtility } from '@chorus/json-request-library';
import { ExpiryDate } from '../Types/Payments/PaymentDetails';
import RejectionHandler from './RejectionHandler';
import { Communication } from '../Modules/GdprModule';

type ApiCommunicationType = 'email' | 'phone' | 'post' | 'sms';

interface ApiDonationRequest {
  amountInPence: number;

  title: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  telephone: string;
  organisation: string;
  address: {
    line1: string;
    line2: string;
    line3: string;
    county: string;
    city: string;
    postCode: string;
    country: string;
    countryCode: string;
  };

  giftAidOptedIn?: boolean;

  productId?: string;
  projectId?: string;

  mapToCampaign?: 'HowHeardAboutUs' | 'ReasonForDonating';

  donationTypeId?: string;
  donationTypeOther?: string;
  howHeardAboutUsId?: string;
  howHeardAboutUsOther?: string;

  communications?: ApiCommunicationPreference[];
  DefaultTheEndDate: boolean | null;
}

type ApiWorldNetDonationRequest = ApiDonationRequest & {
  reference: string;
  cardNumber: string;
  cardExpiry: string;
  cardType: string;
  cardHolderName: string;
  cvv: string;
  mpiRef: string;
  comments: string;
};

type ApiWorldNetRecurringDonationRequest = ApiWorldNetDonationRequest & {
  startDate: Date;
};

interface DonorDetails {
  title: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  telephone: string;
  organisation: string;
  address: {
    postcode: string;
    line1: string;
    line2: string;
    line3: string;
    town: string;
    county: string;
    country: string;
    countryIso: string;
  };
}

interface ApiCommunicationPreference {
  communicationPreferenceId: string;
  communicationPreferenceType: string;
  optedIn: boolean;
}

interface CardDetails {
  cardType: string;
  cardNumber: string;
  cvv: string;
  expiryDate: ExpiryDate;
  cardHolderName: string;
  orderId: string;
  mpiRef: string;
}

export default class WorldNetDonationRepository {
  static MakeDonation = (
    evoApiBaseUrl: string,
    donationDetails: {
      productId: string;
      amountPence: number;
      projectId: string;
    },
    donationTypeDetails: {
      id?: string,
      otherText?: string,
    },
    howHeardAboutUsDetails: {
      id?: string,
      otherText?: string,
    },
    donorDetails: DonorDetails,
    communications: Communication[],
    cardDetails: CardDetails,
    giftAidOptedIn: boolean | undefined,
    donation: {comment: string},
    defaultTheEndDate: boolean | null,
    mapToCampaign?: 'HowHeardAboutUs' | 'ReasonForDonating'
  ): Promise<void> => {

    let mappedCampaignActivity;
    let mappedComment;
    switch (mapToCampaign) {
      case 'ReasonForDonating':
        mappedCampaignActivity = donationTypeDetails;
        mappedComment = howHeardAboutUsDetails.otherText ? `How Heard About Us: ${howHeardAboutUsDetails.otherText}` : null;
        break;
      default:
        mappedCampaignActivity = howHeardAboutUsDetails;
        mappedComment = donationTypeDetails.otherText ? `Donation Type: ${donationTypeDetails.otherText}` : null;
    }
    
    let communicationPreferences: ApiCommunicationPreference[] = [];
    if (communications.length > 0) {

      let groupByCommunications = communications
        .reduce((group: { [key: string]: Communication[] }, item) => {
          if (!group[item.id]) {
            group[item.id] = [];
          }
          group[item.id].push(item);
          return group;
        }, 
                {});

      Object.keys(groupByCommunications).forEach((key) => {
        let channels: ApiCommunicationType[] = [];
        groupByCommunications[key].forEach((preference: Communication) => {
          if (preference.email !== undefined && preference.email) {
            channels.push('email');
          }

          if (preference.phone !== undefined && preference.phone) {
            channels.push('phone');
          }

          if (preference.post !== undefined && preference.post) {
            channels.push('post');
          }

          if (preference.sms !== undefined && preference.sms) {
            channels.push('sms');
          }
        });

        if (channels.length > 0) {
          communicationPreferences.push({
            communicationPreferenceId: key,
            communicationPreferenceType: channels.join(','),
            optedIn: true
          });
        } else {
          communicationPreferences.push({
            communicationPreferenceId: key,
            communicationPreferenceType: 'email,post,sms,phone',
            optedIn: false
          });
        }

      });
    }

    const requestBody: ApiWorldNetDonationRequest = {
      amountInPence: donationDetails.amountPence,

      title: donorDetails.title,
      firstName: donorDetails.firstName,
      lastName: donorDetails.lastName,
      emailAddress: donorDetails.emailAddress,
      telephone: donorDetails.telephone,
      organisation: donorDetails.organisation,
      address: {
        line1: donorDetails.address.line1,
        line2: donorDetails.address.line2,
        line3: donorDetails.address.line3,
        county: donorDetails.address.county,
        city: donorDetails.address.town,
        postCode: donorDetails.address.postcode,
        country: donorDetails.address.country,
        countryCode: donorDetails.address.countryIso,
      },

      productId: donationDetails.productId,
      projectId: donationDetails.projectId,

      howHeardAboutUsId: mappedCampaignActivity.id,
      howHeardAboutUsOther: mappedCampaignActivity.otherText,

      giftAidOptedIn: giftAidOptedIn,

      communications: communicationPreferences,

      reference: cardDetails.orderId,
      cardNumber: cardDetails.cardNumber,
      cardExpiry: cardDetails.expiryDate.month + cardDetails.expiryDate.year,
      cardType: cardDetails.cardType,
      cardHolderName: cardDetails.cardHolderName,
      cvv: cardDetails.cvv,
      mpiRef: cardDetails.mpiRef,
      comments: [
        (mappedComment ? mappedComment : null),
        (donorDetails.organisation ? `Organisation: ${donorDetails.organisation}` : null),
        (donation.comment ? `Comments: ${donation.comment}` : null)
      ].filter(Boolean).join('\n'),
      DefaultTheEndDate: defaultTheEndDate,
    };

    const request = HttpRequestUtility.CreateRequest(
      evoApiBaseUrl + '/api/donations/worldnet/oneoff',
      HttpMethod.POST,
      requestBody
    );

    const createPromise = new Promise<void>((resolve, reject) => {
      HttpRequestUtility.ExecuteJsonRequest(request)
        .then(resolve)
        .catch(e => {
          RejectionHandler.handleReject(reject, e as Response);          
        });
    });

    return createPromise;
  }

  static MakeRecurringDonation = (
    evoApiBaseUrl: string,
    donationDetails: {
      productId: string;
      amountPence: number;
      projectId: string;
      startDate: Date;
    },
    donationTypeDetails: {
      id?: string,
      otherText?: string,
    },
    howHeardAboutUsDetails: {
      id?: string,
      otherText?: string,
    },
    donorDetails: DonorDetails,
    cardDetails: CardDetails,
    giftAidOptedIn: boolean | undefined,
    communications: Communication[],
    donation: {comment: string},
    defaultTheEndDate: boolean | null,
    mapToCampaign?: 'HowHeardAboutUs' | 'ReasonForDonating'
  ): Promise<void> => {

    let mappedCampaignActivity;
    let mappedComment;
    switch (mapToCampaign) {
      case 'ReasonForDonating':
        mappedCampaignActivity = donationTypeDetails;
        mappedComment = howHeardAboutUsDetails.otherText ? `How Heard About Us: ${howHeardAboutUsDetails.otherText}` : null;
        break;
      default:
        mappedCampaignActivity = howHeardAboutUsDetails;
        mappedComment = donationTypeDetails.otherText ? `Donation Type: ${donationTypeDetails.otherText}` : null;
    }

    let communicationPreferences: ApiCommunicationPreference[] = [];
    if (communications.length > 0) {

      let groupByCommunications = communications
        .reduce((group: { [key: string]: Communication[] }, item) => {
          if (!group[item.id]) {
            group[item.id] = [];
          }
          group[item.id].push(item);
          return group;
        },      {});

      Object.keys(groupByCommunications).forEach((key) => {
        let channels: ApiCommunicationType[] = [];
        groupByCommunications[key].forEach((preference: Communication) => {

          if (preference.email !== undefined && preference.email) {
            channels.push('email');
          }

          if (preference.phone !== undefined && preference.phone) {
            channels.push('phone');
          }

          if (preference.post !== undefined && preference.post) {
            channels.push('post');
          }

          if (preference.sms !== undefined && preference.sms) {
            channels.push('sms');
          }
        });

        if (channels.length > 0) {
          communicationPreferences.push({
            communicationPreferenceId: key,
            communicationPreferenceType: channels.join(','),
            optedIn: true
          });
        } else {
          communicationPreferences.push({
            communicationPreferenceId: key,
            communicationPreferenceType: 'email,post,sms,phone',
            optedIn: false
          });
        }

      });
    }
    const requestBody: ApiWorldNetRecurringDonationRequest = {
      amountInPence: donationDetails.amountPence,

      title: donorDetails.title,
      firstName: donorDetails.firstName,
      lastName: donorDetails.lastName,
      emailAddress: donorDetails.emailAddress,
      telephone: donorDetails.telephone,
      organisation: donorDetails.organisation,
      address: {
        line1: donorDetails.address.line1,
        line2: donorDetails.address.line2,
        line3: donorDetails.address.line3,
        county: donorDetails.address.county,
        city: donorDetails.address.town,
        postCode: donorDetails.address.postcode,
        country: donorDetails.address.country,
        countryCode: donorDetails.address.countryIso,
      },

      productId: donationDetails.productId,
      projectId: donationDetails.projectId,

      howHeardAboutUsId: mappedCampaignActivity.id,
      howHeardAboutUsOther: mappedCampaignActivity.otherText,

      giftAidOptedIn: giftAidOptedIn,

      communications: communicationPreferences,

      reference: cardDetails.orderId,
      cardNumber: cardDetails.cardNumber,
      cardExpiry: cardDetails.expiryDate.month + cardDetails.expiryDate.year,
      cardType: cardDetails.cardType,
      cardHolderName: cardDetails.cardHolderName,
      cvv: cardDetails.cvv,
      mpiRef: cardDetails.mpiRef,

      startDate: donationDetails.startDate,
      comments: [
        (mappedComment ? mappedComment : null),
        (donorDetails.organisation ? `Organisation: ${donorDetails.organisation}` : null),
        (donation.comment ? `Comments: ${donation.comment}` : null)
      ].filter(Boolean).join('\n'),
      DefaultTheEndDate: defaultTheEndDate,
    };

    const request = HttpRequestUtility.CreateRequest(
      evoApiBaseUrl + '/api/donations/worldnet/recurring',
      HttpMethod.POST,
      requestBody
    );

    const createPromise = new Promise<void>((resolve, reject) => {
      HttpRequestUtility.ExecuteJsonRequest(request)
        .then(resolve)
        .catch(e => RejectionHandler.handleReject(reject, e as Response));
    });

    return createPromise;
  }
}



// WEBPACK FOOTER //
// ./src/Helpers/WorldNetDonationRepository.tsx